import { useEffect, useRef } from 'react';
import './Privacy.css';

// Bump this string whenever the policy materially changes.
// user_accounts.consent_version stores what each user agreed to.
export const PRIVACY_VERSION = '2026-09-10';

const CONTACT_EMAIL = 'phantomghostsesportsclub@gmail.com';

function Privacy() {
  const originalBodyClass = useRef('');

  useEffect(() => {
    const body = document.querySelector('body');
    originalBodyClass.current = body.className;
    body.className = 'no-bg';
    window.scrollTo(0, 0);
    return () => {
      body.className = originalBodyClass.current;
    };
  }, []);

  return (
    <div className="privacy-root">
      <div className="privacy-container">

        <header className="privacy-header">
          <h1 className="privacy-title">Privacy Policy</h1>
          <p className="privacy-meta">
            PG Esports · Valorant Premier Legends<br />
            Last updated: 10 September 2026
          </p>
          <div className="privacy-scanline" />
        </header>

        <section className="privacy-section">
          <p className="privacy-lead">
            This policy explains what information PG Esports collects when you use
            pgesports.in, why we collect it, and what control you have over it. We
            run a community Valorant league. We are not in the business of selling
            data and we never will be.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Who we are</h2>
          <p>
            PG Esports operates the Valorant Premier Legends (VPL), a community
            esports league based in Hyderabad, Telangana, India. For any question
            about this policy or your data, write to{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </section>

        <section className="privacy-section">
          <h2>What we collect</h2>

          <h3>When you create an account</h3>
          <p>
            We use Google Sign-In. Google gives us your email address, your name,
            and your profile picture. We never see or store your Google password.
          </p>

          <h3>When you register for a VPL season</h3>
          <ul>
            <li>Your Riot ID and the associated PUUID (a permanent Valorant account identifier)</li>
            <li>Your in-game name, current rank and peak rank</li>
            <li>Your preferred roles</li>
            <li>Name, email address and phone number</li>
            <li>Discord username and Instagram handle, if you provide them</li>
            <li>City and state</li>
            <li>A player photo, if you upload one</li>
            <li>A tracker profile link, if you provide one</li>
          </ul>

          <h3>Match and performance data</h3>
          <p>
            For matches played in VPL tournaments we record kills, deaths, assists,
            combat score, accuracy, ability usage, agents played, maps and results.
            This comes from the Valorant match data associated with your Riot
            account. It is competitive league data and it is published on the site.
          </p>

          <h3>Technical data</h3>
          <p>
            Our servers log IP addresses and browser information for security and
            abuse prevention. We do not use advertising trackers or sell any
            information to advertisers.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Why we collect it</h2>
          <ul>
            <li><strong>To run the league</strong> — verifying Valorant accounts, seeding the auction, building fixtures, publishing standings.</li>
            <li><strong>To keep your history</strong> — so your stats stay attached to you across seasons even if you change or replace your Riot account.</li>
            <li><strong>To contact you</strong> — match schedules, roster confirmations, season announcements.</li>
            <li><strong>To run community features</strong> — leaderboards, player profiles and other free community activities on the site.</li>
            <li><strong>To keep things fair</strong> — detecting duplicate accounts and reviewing claims to past-season statistics.</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>What is public</h2>
          <p>These appear publicly on pgesports.in:</p>
          <ul>
            <li>In-game name and Riot ID</li>
            <li>Rank, roles and team</li>
            <li>Match statistics and season standings</li>
            <li>Player photo, if you uploaded one and it was approved</li>
            <li>City and state</li>
          </ul>
          <p className="privacy-callout">
            Your email address and phone number are never shown publicly. They are
            used only by league administrators to contact you.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Who we share it with</h2>
          <p>We do not sell your data. We share it only with services required to run the site:</p>
          <ul>
            <li><strong>Supabase</strong> — database and account authentication</li>
            <li><strong>Amazon Web Services</strong> — hosting and file storage</li>
            <li><strong>Google</strong> — sign-in only</li>
            <li><strong>HenrikDev API</strong> — a third-party service we query for public Valorant account and match information</li>
          </ul>
          <p>
            Team owners and captains can see contact details for players on their own
            roster, so they can organise matches.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Players under 18</h2>
          <p>
            If you are under 18, please get a parent or guardian's permission before
            creating an account or registering for a season. We ask for date of
            birth so we can apply appropriate protections.
          </p>
          <p>
            For accounts belonging to minors we do not publish city, state or photos
            without parental consent, and we do not use contact details for anything
            beyond league administration. A parent or guardian can email us at any
            time to review or delete their child's information.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Your rights</h2>
          <p>
            Under India's Digital Personal Data Protection Act, 2023 you can ask us to:
          </p>
          <ul>
            <li>Show you what data we hold about you</li>
            <li>Correct anything that is wrong</li>
            <li>Delete your account and personal information</li>
            <li>Withdraw consent for us to process your data</li>
          </ul>
          <p>
            Email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we will
            respond within 30 days.
          </p>
          <p className="privacy-callout">
            One limit worth being upfront about: if you delete your account we remove
            your personal details, but match results and standings from tournaments
            you competed in remain part of the league's competitive record. Removing
            them would rewrite results that other players and teams also took part in.
          </p>
        </section>

        <section className="privacy-section">
          <h2>How long we keep it</h2>
          <ul>
            <li><strong>Account and contact details</strong> — until you ask us to delete them</li>
            <li><strong>Match statistics</strong> — kept permanently as part of league history</li>
            <li><strong>Server logs</strong> — around 90 days</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>Security</h2>
          <p>
            Data is stored in encrypted databases with restricted access. Sign-in is
            handled by Google and Supabase, so we never handle your password.
            Administrative actions are logged. No system is perfectly secure, and if
            a breach ever affects your data we will notify you and the relevant
            authority.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Cookies</h2>
          <p>
            We use only what is necessary to keep you signed in. No advertising
            cookies, no third-party tracking pixels.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Changes</h2>
          <p>
            If we make a material change we will update the date at the top of this
            page and let signed-in users know. Continuing to use the site after a
            change means you accept the updated policy.
          </p>
        </section>

        <footer className="privacy-footer">
          <p>
            Questions about your data?<br />
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </p>
          <p className="privacy-version">Version {PRIVACY_VERSION}</p>
        </footer>

      </div>
    </div>
  );
}

export default Privacy;